import { useSentryQuery, useSentryMutation } from '../utils/sentryQueryWrapper';
import {
  createRemittance,
  deleteRemittance,
  fetchAvailableCod,
  fetchFinanceOverview,
  FinanceOverviewResponse,
  fetchRemittanceActivityLogByRemittanceUID,
  fetchRemittanceByUID,
  fetchRemittances,
  fetchRemittanceUpdatesCommentByRemittanceUID,
  fetchTransactions,
  updateRemittanceStatus,
} from '@/api/remittanceService';
import {
  ActivityLogResponse,
  AvailableCodResponse,
  ErrorMessageResponse,
  Remittance,
  RemittanceListResponse,
  RemittanceResponse,
  RemittanceSingleResponse,
  TranslationListResponse,
} from '@/types';

type RemittanceStatus = 'initiated' | 'in-transit' | 'delivered';

// Fetch all remittances with pagination
export const useRemittances = (page: number, pageSize?: number, enabled?: boolean, statusFilter?: string, dateFrom?: string, dateTo?: string) =>
  useSentryQuery<RemittanceListResponse, ErrorMessageResponse>(
    ['remittances', page, statusFilter, dateFrom, dateTo],
    () => fetchRemittances(page, pageSize, statusFilter, dateFrom, dateTo),
    { enabled },
  );

// Fetch Remittance UpdatesComments (single page, with optional pagination params)
export const useFetchRemittanceUpdatesCommentByRemittanceUID = (
  uid: string,
  lang: string,
  page = 1,
  perpage = 50,
) => {
  return useSentryQuery<ActivityLogResponse, Error>(
    [`remittance-recalculate-transaction`, uid, lang, page, perpage],
    () => fetchRemittanceUpdatesCommentByRemittanceUID(uid, page, perpage),
    { enabled: !!uid },
  );
};
// Fetch Remittance ActivityLogs - single page only
export const useFetchRemittanceActivityLogByRemittanceUID = (
  uid: string,
  lang: string,
  page: number,
  perpage = 50,
) => {
  return useSentryQuery<ActivityLogResponse, Error>(
    [`remittance-activity-log`, uid, lang, page, perpage],
    () => fetchRemittanceActivityLogByRemittanceUID(uid, page, perpage),
    { enabled: !!uid },
  );
};

// Fetch all transactions
export const useTransactions = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  language?: string,
  transactionParams?: URLSearchParams,
) => {
  return useSentryQuery<TranslationListResponse, ErrorMessageResponse>(
    ['transactions', page, language, transactionParams?.toString()],
    () => fetchTransactions(page, pageSize, transactionParams),
    { enabled },
  );
};
// Fetch a single remittance by UID
export const useFetchRemittanceByUID = (uid: string) =>
  useSentryQuery<RemittanceSingleResponse, ErrorMessageResponse>(
    ['remittance', uid],
    () => fetchRemittanceByUID(uid),
    { enabled: !!uid },
  );

// Create a new remittance
export const useCreateRemittance = () =>
  useSentryMutation<RemittanceResponse, ErrorMessageResponse, Remittance>(
    (remittance: Remittance) => createRemittance(remittance),
  );

// Delete a remittance by UID
export const useDeleteRemittance = () =>
  useSentryMutation<void, ErrorMessageResponse, number>((uid: number) => deleteRemittance(uid));

// Update the status of an existing remittance
export const useUpdateRemittanceStatus = () =>
  useSentryMutation<void, ErrorMessageResponse, { uid: number; status: RemittanceStatus }>(
    ({ uid, status }) => updateRemittanceStatus(uid, status),
  );

// Fetch available COD
export const useFetchAvailableCod = (enabled = true) => {
  const query = useSentryQuery<AvailableCodResponse, ErrorMessageResponse>(
    ['availableCod'],
    () => fetchAvailableCod(),
    { enabled },
  );

  // When query is disabled, React Query returns isLoading: true by default
  // Override this to return false when disabled
  return {
    ...query,
    isLoading: enabled ? query.isLoading : false,
  };
};

// Finance overview (balance, total sales, chart)
export const useFetchFinanceOverview = (enabled: boolean = true, period?: string) =>
  useSentryQuery<FinanceOverviewResponse, ErrorMessageResponse>(
    ["finance-overview", period],
    () => fetchFinanceOverview(period),
    { enabled, staleTime: 60000 },
  );
